import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Send, Loader2, Trash2, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { engageApi } from "@/lib/engageApi";
import { useI18n } from "@/lib/i18n";
import { StatusBadge } from "@/components/find/atoms";

const CAMPAIGN_STATUS = {
    draft: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
    active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    paused: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    completed: "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

export default function CampaignsList() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { load(); }, []);
    function load() {
        setLoading(true);
        engageApi.campaigns.list()
            .then(setItems)
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    }

    async function togglePause(c) {
        try {
            const next = c.status === "active" ? "paused" : "active";
            await engageApi.campaigns.update(c.campaign_id, { status: next });
            load();
        } catch (_e) { toast.error("Failed"); }
    }
    async function handleDelete(c) {
        if (!window.confirm(`Delete "${c.name}"?`)) return;
        try {
            await engageApi.campaigns.remove(c.campaign_id);
            toast.success("Campaign deleted");
            load();
        } catch (_e) { toast.error("Failed"); }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="font-display font-black text-3xl tracking-tighter">{t.engage.campaigns.title}</h1>
                    <p className="mt-1 text-sm text-zinc-500 max-w-2xl">{t.engage.campaigns.subtitle}</p>
                </div>
                <Link
                    to="/app/engage/campaigns/new"
                    data-testid="campaigns-create-btn"
                    className="btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2"
                >
                    <Plus size={14} /> {t.engage.campaigns.create}
                </Link>
            </div>

            {loading ? (
                <div className="py-24 flex items-center justify-center text-zinc-500 text-sm">
                    <Loader2 size={18} className="animate-spin mr-2" /> Loading…
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-2xl p-12 text-center bg-brand-surface border border-white/8">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center mb-3">
                        <Send size={22} className="text-white" />
                    </div>
                    <h3 className="font-display font-bold text-xl">{t.engage.campaigns.empty}</h3>
                    <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">{t.engage.campaigns.emptyBody}</p>
                    <Link
                        to="/app/engage/campaigns/new"
                        className="mt-6 inline-block btn-cta text-white font-semibold text-sm px-5 py-2.5 rounded-full"
                    >
                        {t.engage.campaigns.create}
                    </Link>
                </div>
            ) : (
                <div className="rounded-2xl border border-white/8 bg-brand-surface overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-wide text-zinc-500 border-b border-white/8 bg-white/[0.02]">
                                <tr>
                                    <th className="p-3 text-left">{t.engage.campaigns.cols.name}</th>
                                    <th className="p-3 text-left">{t.engage.campaigns.cols.leads}</th>
                                    <th className="p-3 text-left">{t.engage.campaigns.cols.sent}</th>
                                    <th className="p-3 text-left hidden md:table-cell">{t.engage.campaigns.cols.open}</th>
                                    <th className="p-3 text-left hidden md:table-cell">{t.engage.campaigns.cols.reply}</th>
                                    <th className="p-3 text-left">{t.engage.campaigns.cols.status}</th>
                                    <th className="p-3 w-32"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => (
                                    <tr
                                        key={c.campaign_id}
                                        className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                                        data-testid={`campaign-row-${c.campaign_id}`}
                                        onClick={() => navigate(`/app/engage/campaigns/${c.campaign_id}`)}
                                    >
                                        <td className="p-3">
                                            <div className="text-white font-medium">{c.name}</div>
                                            <div className="text-xs text-zinc-500">{c.steps?.length || 0} steps · sender: {c.sender_name}</div>
                                        </td>
                                        <td className="p-3 font-mono text-zinc-300">{c.lead_ids?.length || 0}</td>
                                        <td className="p-3 font-mono text-white">{c.stats?.sent || 0}</td>
                                        <td className="p-3 font-mono text-zinc-300 hidden md:table-cell">{Math.round((c.stats?.open_rate || 0) * 100)}%</td>
                                        <td className="p-3 font-mono text-zinc-300 hidden md:table-cell">{Math.round((c.stats?.reply_rate || 0) * 100)}%</td>
                                        <td className="p-3">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft}`}>
                                                {t.engage.campaigns.statusLabels[c.status] || c.status}
                                            </span>
                                        </td>
                                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center gap-2 justify-end">
                                                {(c.status === "active" || c.status === "paused") && (
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePause(c)}
                                                        data-testid={`campaign-toggle-${c.campaign_id}`}
                                                        className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white"
                                                        title={c.status === "active" ? "Pause" : "Resume"}
                                                    >
                                                        {c.status === "active" ? <Pause size={14} /> : <Play size={14} />}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(c)}
                                                    data-testid={`campaign-delete-${c.campaign_id}`}
                                                    className="p-1.5 rounded hover:bg-rose-500/10 text-zinc-400 hover:text-rose-300"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
